import { IonCard, IonIcon } from "@ionic/react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { type Shift } from "@/lib/backend/api";
import { chevronRightIcon } from "@/lib/icons/chevron-right.icon";
import { hourglassIcon } from "@/lib/icons/hourglass.icon";
import { cx } from "@/lib/style/cva.config";
import { formatInTz } from "@/utils/format-datetime";
import { calculateShiftDuration, formatDurationToISO } from "@/utils/time";

export const ShiftCardCompleted = forwardRef<
  HTMLIonCardElement,
  React.ComponentProps<typeof IonCard> & { shift: Shift }
>(function ShiftCardCompleted({ shift, ...props }, ref) {
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const hasEntry = !shift.shiftTimeEntry;

  const startAt =
    typeof shift.shiftTimeEntry?.startAt === "string"
      ? shift.shiftTimeEntry.startAt
      : shift.startAt;
  const endAt = shift.shiftTimeEntry?.endAt ?? shift.endAt;

  const duration = calculateShiftDuration(
    startAt,
    endAt,
    shift.shiftTimeEntry?.breakTimeMinutes ?? 0,
  );

  return (
    <IonCard
      ref={ref}
      {...props}
      className={cx(
        "border-button-primary p-4 part-native:flex part-native:items-center part-native:gap-3",
        props.className,
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex min-w-0 flex-col">
          <h3 className="truncate text-lg font-bold">{shift.project?.name}</h3>

          <time dateTime={startAt} className="font-medium text-txt-secondary">
            {formatInTz(startAt, {
              day: "numeric",
              month: "long",
              year: "numeric",
              locale: i18n.language,
              timeZone: shift.timeZone ?? shift.project?.timeZone,
            })}
          </time>
        </div>

        <div className="flex items-center gap-2 font-bold text-txt-primary">
          <IonIcon icon={hourglassIcon} className="size-6" />
          {hasEntry && "~"}
          <time dateTime={formatDurationToISO(duration)} className="inline-flex gap-1">
            <span>
              {Intl.NumberFormat(i18n.language, {
                style: "unit",
                unit: "hour",
                unitDisplay: "narrow",
              })
                .formatToParts(duration.hours)
                .filter((part) => part.type !== "literal" && part.value !== " ")
                .map((part) => part.value)
                .join("")}
            </span>
            <span>
              {Intl.NumberFormat(i18n.language, {
                style: "unit",
                unit: "minute",
                unitDisplay: "narrow",
              })
                .formatToParts(duration.minutes)
                .filter((part) => part.type !== "literal" && part.value !== " ")
                .map((part) => part.value)
                .join("")}
            </span>
          </time>
          {!!hasEntry && (
            <span className="text-sm font-normal text-txt-primary">
              {t("common:indicate-hours")}
            </span>
          )}
        </div>
      </div>

      <IonIcon icon={chevronRightIcon} className="size-6" />
    </IonCard>
  );
});
